import { inject, Injectable } from '@angular/core';
import {
  DisplayUser,
  FolderDto,
  FolderPublicAccessLevel,
  FolderTenantAccessLevel,
  TenantDto,
} from '@nusign/picflow-backend-types';
import { firstValueFrom, from, map, Observable, of, switchMap } from 'rxjs';
import { billingScopes } from '../billing-consts';
import { accessLevelOrder } from '../consts';
import { FolderAccessLevel } from '../lib/access-level';
import { forkJoinSafe } from '../lib/safe-fork-join';
import { unique } from '../lib/unique';
import { PermissionService } from './permission.service';
import { StateService } from './state.service';
import { TenantService } from './tenant.service';
import { UserGroupService } from './user-group.service';

@Injectable({
  providedIn: 'root',
})
export class FolderAccessLevelSubService {
  private state = inject(StateService);
  private tenantService = inject(TenantService);
  private permissionService = inject(PermissionService);
  private userGroupService = inject(UserGroupService);

  public isFolderGuest(
    folder: FolderDto,
    sharedWithMeFolders: (FolderDto & { access_level: FolderAccessLevel })[]
  ): Observable<boolean> {
    if (!this.state.user) return of(false);
    const folderTenantId = (folder.tenant as TenantDto)?.id || (folder.tenant as string);
    if (this.state.tenants?.find(t => t.id === folderTenantId)) return of(false);
    return of(sharedWithMeFolders.some(g => g.id === folder.id));
  }

  public async getAccessLevel(
    folder: FolderDto,
    folderChain: FolderDto[],
    sharedWithMeFolders: (FolderDto & { access_level: FolderAccessLevel })[],
    options: { ignorePublicAccessLevel?: boolean } = {}
  ): Promise<FolderAccessLevel> {
    const galleryTenantId = (folder.tenant as TenantDto)?.id || (folder.tenant as string);

    const [tenantAccessLevel, _publicAccessLevel] = await this.getAccessLevels(folderChain);
    if (!this.state.user) return _publicAccessLevel || 'no_access'; // handling not logged in users
    let galleryGroupAccessLevel: FolderAccessLevel = 'no_access';

    const publicAccessLevel = options.ignorePublicAccessLevel ? 'no_access' : _publicAccessLevel;

    if (!galleryTenantId) {
      console.warn('Gallery doesnt contain a tenant ID, falling back to tenant access level', folderChain);
      return this.returnHighest([tenantAccessLevel, publicAccessLevel]);
    }

    // Checking if you are part of the team
    const isPartOfTeam = this.state.tenants.find(t => t.id === galleryTenantId);

    // If this user is the creator of the gallery, they should always have edit access
    if (this.state.user.id === ((folder.created_by as DisplayUser)?.id || (folder.created_by as string))) return 'edit';

    if (isPartOfTeam) {
      // Making sure that the galleries tenant is the active one
      const activeTenant = this.state.activeTenant$?.value;
      if (activeTenant?.id !== galleryTenantId) {
        await this.tenantService.setActiveTenant(isPartOfTeam);
      }

      // If the user is an admin or the owner, they always have edit access
      if (this.state.activeTenantRole === 'admin' || this.state.activeTenantRole === 'owner') {
        return 'edit';
      }

      galleryGroupAccessLevel = await firstValueFrom(this.getMaxAccessLevelForGalleryUserGroupsUsers(folderChain));
    }

    // Shortcut to avoid requesting member list when not needed
    if ((tenantAccessLevel === 'edit' || galleryGroupAccessLevel === 'edit') && isPartOfTeam) {
      return this.returnHighest([tenantAccessLevel, galleryGroupAccessLevel]);
    }

    const sharedWithMeFolder = sharedWithMeFolders.find(f => f.id === folder.id);

    if (sharedWithMeFolder) {
      return this.returnHighest([publicAccessLevel, sharedWithMeFolder.access_level]);
    }

    // Fallback for public access
    if (!isPartOfTeam) return this.returnHighest([publicAccessLevel]);

    // Fallback for team members
    return this.returnHighest([tenantAccessLevel, publicAccessLevel, galleryGroupAccessLevel]);
  }

  private returnHighest(accessLevels: FolderAccessLevel[]): FolderAccessLevel {
    accessLevels.sort((a, b) => (accessLevelOrder.indexOf(a) < accessLevelOrder.indexOf(b) ? 1 : -1));
    return accessLevels[0] || 'no_access';
  }

  public async getAccessLevels(folderChain: FolderDto[]): Promise<[FolderTenantAccessLevel, FolderPublicAccessLevel]> {
    const firstWithAccessLevels =
      folderChain.find(f => f.tenant_access && f.public_access) ||
      folderChain.find(f => f.tenant_access || f.public_access);
    if (!firstWithAccessLevels) return ['edit', 'no_access'];
    return [firstWithAccessLevels?.tenant_access, firstWithAccessLevels?.public_access];
  }

  private getMaxAccessLevelForGalleryUserGroupsUsers(folderChain: FolderDto[]): Observable<FolderAccessLevel> {
    if (!this.permissionService.canDisplayElement(billingScopes.groups.list)) return of('no_access');

    return forkJoinSafe(folderChain.map(folder => from(this.userGroupService.getFolderUserGroups(folder.id)))).pipe(
      map(groups => unique(groups.flat(), 'id')),
      switchMap(groups => {
        return forkJoinSafe(
          groups.map(group =>
            from(this.userGroupService.getUserGroupUsers(group.group)).pipe(map(users => ({ users, group })))
          )
        ).pipe(
          map(datas => {
            return datas.map(data => {
              if (data.users.includes(this.state.user.id)) return data.group.access;
            });
          })
        );
      }),
      map(accessLevels => this.returnHighest(accessLevels))
    );
  }
}
